// P2-INSTALL — THE GLOWY POP-UP. Centered, glowing, house-styled, per the P2-2 popup law.
//
// **THE FUNNEL'S MISSING FIRST HOP.** Tina's ask-check receipt named the condition: `installed:
// false`, both standalone signals false, so the push ask refused — correctly, because on iOS web push
// exists only inside the installed app. Nothing anywhere told her to open the app rather than the
// site. This is that missing thing, and every one of the 300 walks the same path.
//
// The LAW is `src/installPrompt.js` (when to offer, the platform split, the copy, the plan) and it is
// RUN by `scripts/pi1-install-prompt.mjs`. This file is the part that needs a browser.
import { useCallback, useEffect, useRef, useState } from "react";
import { Share, Plus, Sparkles } from "lucide-react";
import {
  INSTALL_REOFFER_EVENT,
  INSTALL_ANSWERS, INSTALL_KEYS, INSTALL_HANDOFF, PLATFORM,
  announceInstalled, installHandoff, installPlan, platformOf, runInstallOffer,
} from "../installPrompt.js";
import { t } from "../strings.js";

const post = (url: string, body: unknown) =>
  fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) })
    .then((r) => r.json()).catch(() => null);

type BeforeInstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

export function InstallPrompt({
  signedIn, hasRoom, forceOpen = false, onClose, language = "en",
}: { signedIn: boolean; hasRoom: boolean; forceOpen?: boolean; onClose?: () => void; language?: string }) {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState(PLATFORM.OTHER);
  const offered = useRef(false);
  // **ANDROID'S REAL PROMPT ARRIVES AS AN EVENT AND MUST BE CAUGHT BEFORE IT IS NEEDED.** The browser
  // fires it once, early; a page that only starts listening when it wants to install has already
  // missed it. Held here so the tap can use it.
  const androidEvent = useRef<BeforeInstallPromptEvent | null>(null);
  // >>> **STATE AS WELL AS A REF, AND THAT IS NOT A DUPLICATE.** <<< The ref is what the TAP needs;
  // this is what the RENDER needs, and a ref does not re-render. Chrome fires
  // `beforeinstallprompt` when it decides the site qualifies, which is routinely AFTER this prompt
  // has opened — so with the ref alone the plan is computed once, reads "no real prompt", and can
  // never upgrade to the button even though the event is now sitting in hand.
  const [promptReady, setPromptReady] = useState(false);
  // **A FAILED PROMPT FALLS BACK TO THE INSTRUCTIONS RATHER THAN CLOSING** — see the tap below.
  const [fellBack, setFellBack] = useState(false);

  useEffect(() => {
    const grab = (e: Event) => {
      e.preventDefault();
      androidEvent.current = e as BeforeInstallPromptEvent;
      setPromptReady(true);
    };
    window.addEventListener("beforeinstallprompt", grab);
    return () => window.removeEventListener("beforeinstallprompt", grab);
  }, []);

  useEffect(() => { if (forceOpen) { setPlatform(platformOf(window)); setOpen(true); } }, [forceOpen]);

  // INSTALL-1 CLAUSE 7 — **HER RE-OFFER, ANNOUNCED RATHER THAN THREADED.** The account menu is a
  // portal and is not in this component's tree, so a flag would have to be lifted into App.tsx
  // and passed back down — putting the decision inside an eleven-thousand-line file no gate can
  // reach (R8/R95). `CauldronDoor`'s own shape, and the listener is REMOVED on unmount: one that
  // outlives its mount fires her sheet once per mount this component has ever had.
  useEffect(() => {
    const onAsk = () => { setPlatform(platformOf(window)); setOpen(true); };
    window.addEventListener(INSTALL_REOFFER_EVENT, onAsk);
    return () => window.removeEventListener(INSTALL_REOFFER_EVENT, onAsk);
  }, []);

  useEffect(() => {
    if (offered.current || forceOpen) return;
    if (!signedIn) return;
    let alive = true;
    (async () => {
      // The DECIDING and the REPORTING are the law's (`runInstallOffer`), so P3-2B's lesson — the
      // reasons are reported, refusals included — is RUN rather than being a line order in here.
      const decision = await runInstallOffer({
        win: window,
        hasRoom,
        readState: () => fetch("/api/install/state").then((x) => x.json()).catch(() => null),
        report: (payload) => post("/api/install/seen", payload),
        alive: () => alive,
        promptReady: androidEvent.current !== null,
      });
      if (!alive || !decision.offer) return;
      offered.current = true;
      setPlatform(decision.platform);
      setOpen(true);
    })();
    return () => { alive = false; };
  }, [signedIn, hasRoom, forceOpen]);

  const close = useCallback(async (answer: string) => {
    setOpen(false);
    onClose?.();
    // ITEM 7(a) — **INSTALL, THEN NOTIFICATIONS, ONE WARM MOTION.** The announcement goes out BEFORE
    // the answer is posted, and that ordering is the clause: the post is a round trip and the ask is
    // her one warm moment, so putting a network call in front of it means a slow minute becomes a
    // gap she fills by leaving. `installHandoff` decides whether anything follows — a member who
    // said *not now* announces NOTHING, because a permission prompt on the back of a refusal spends
    // the one irreversible chance this house has (W13-1b's opening law).
    // The law decides, never a comparison typed here: `installHandoff` answers NONE for every answer
    // that is not an install, and re-spelling that test at the call site is how the ruling above
    // comes to be true in one place and not the other (R28/R29). `pushHere` is the LISTENER's fact,
    // so this end asks the same law without it and only ever distinguishes *something follows* from
    // *nothing does*.
    if (installHandoff({ answer }) !== INSTALL_HANDOFF.NONE) announceInstalled(window);
    // ONE-TIME per the pointer rules, and re-offerable from My Room settings — which is why the
    // answer lives on the ACCOUNT rather than in this device's storage.
    if (!forceOpen) await post("/api/install/answer", { answer });
  }, [forceOpen, onClose]);

  if (!open) return null;
  const plan = installPlan(platform, promptReady && !fellBack);

  return (
    <div className="install-pop__backdrop" role="dialog" aria-modal="true" aria-labelledby="install-pop-title">
      <div className="install-pop">
        <span className="install-pop__glow" aria-hidden="true" />
        <span className="install-pop__mark" aria-hidden="true"><Sparkles size={22} /></span>
        {/* INSTALL-1 clause 1: THE ONE-LINE ASK. A title, a body, a lead and a why-paragraph all met
            her at once; it is one sentence now, and the reason IS the persuasion. It is still the
            dialog's label, so the outline and the screen reader keep a name (R43). */}
        <h2 id="install-pop-title" className="install-pop__title">{t(language, INSTALL_KEYS.ask)}</h2>

        {plan.kind === "prompt" ? (
          <button
            type="button"
            className="install-pop__go"
            // >>> **A TAP ON *ADD IT* THAT CANNOT PROMPT MAY NEVER BE RECORDED AS A REFUSAL.** <<<
            // This branch used to `close(LATER)` on both failure paths, and `later` is a permanent
            // suppression on her account — so a member who said YES was stamped as having declined
            // and could never be offered again, with nothing on screen and nothing in any log to say
            // so. **That is F7's own report from the inside: for her, the popup never appears again.**
            // It falls back to the instructions instead, in place, stamping NOTHING: she keeps the
            // offer and she gets a route she can follow.
            onClick={async () => {
              const ev = androidEvent.current;
              if (!ev) { setFellBack(true); return; }
              try {
                await ev.prompt();
                const choice = await ev.userChoice;
                // Her DECLINING the browser's own dialog is a real answer and is recorded as one.
                await close(choice?.outcome === "accepted" ? INSTALL_ANSWERS.INSTALLED : INSTALL_ANSWERS.LATER);
              } catch { setFellBack(true); }
            }}
          >
            {t(language, plan.action)}
          </button>
        ) : (
          // THE TEACHING, WITH THE TINY VISUAL she asked for: the Share glyph is the one thing a
          // member has to FIND on the screen, so it is shown rather than described. The LEAD line is
          // gone with clause 1's one-line ask — the steps are numbered and speak for themselves.
          <div className="install-pop__teach">
            <ol className="install-pop__steps">
              <li><span className="install-pop__glyph" aria-hidden="true"><Share size={16} /></span>{t(language, plan.steps[0])}</li>
              <li><span className="install-pop__glyph" aria-hidden="true"><Plus size={16} /></span>{t(language, plan.steps[1])}</li>
            </ol>
          </div>
        )}

        <button type="button" className="install-pop__later" onClick={() => close(INSTALL_ANSWERS.LATER)}>
          {t(language, INSTALL_KEYS.later)}
        </button>
      </div>
    </div>
  );
}
